import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Download, Search, FileText, ExternalLink, X, Tag as TagIcon } from "lucide-react";
import { publications, publicationTags } from "@/data/publications";
import { IyafWordmark } from "@/components/brand/IyafMark";

export default function Publications() {
  const { t } = useTranslation();
  const publicationTypes = [
    { key: "all", label: t("publications.filterAll"), color: "#111827" },
    { key: "iyaf", label: t("publications.filterIyaf"), color: "#3663A8" },
    { key: "partner", label: t("publications.filterPartner"), color: "#FF9F1C" },
  ];
  const [type, setType] = useState("all");
  const [activeTags, setActiveTags] = useState([]);
  const [query, setQuery] = useState("");

  const toggleTag = (tag) =>
    setActiveTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  const clearTags = () => setActiveTags([]);

  const hasPublished = publications.length > 0;

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return publications.filter((p) => {
      if (type !== "all" && p.type !== type) return false;
      if (activeTags.length && !activeTags.every((t) => p.tags.includes(t))) return false;
      if (q && !(`${p.title} ${p.source} ${p.brief}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [type, activeTags, query]);

  return (
    <div className="relative" data-testid="publications-page">
      {/* Hero */}
      <section className="pt-16 pb-10">
        <div className="container-iyaf">
          <p className="eyebrow text-iyaf-blue mb-5">{t("publications.eyebrow")}</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95] max-w-4xl text-balance">
            {t("publications.titleLine1")} <span className="font-editorial italic font-normal">{t("publications.titleLine2Italic")}</span>.
          </h1>
          <p className="mt-8 font-editorial text-2xl text-iyaf-ink/75 leading-snug max-w-3xl">
            {t("publications.lead")}
          </p>
        </div>
      </section>

      {/* Sticky filter bar */}
      <section className="py-5 sticky top-16 sm:top-20 z-30 bg-iyaf-paper/90 backdrop-blur-md border-y border-black/5">
        <div className="container-iyaf flex flex-col gap-3">
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <div className="flex flex-wrap gap-2" data-testid="pub-type-filters">
              {publicationTypes.map((f) => {
                const active = type === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => setType(f.key)}
                    data-testid={`pub-type-${f.key}`}
                    className="px-4 py-2 rounded-full text-xs font-semibold transition-all"
                    style={active ? { background: f.color, color: "#fff" } : { background: "white", border: "1px solid rgba(0,0,0,0.08)", color: "rgba(17,24,39,0.7)" }}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
            <div className="relative flex items-center">
              <Search size={14} className="absolute left-3 text-iyaf-ink/40" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="pub-search"
                placeholder={t("publications.search")}
                className="pl-9 pr-3 py-2 rounded-full text-xs bg-white border border-black/10 w-64 focus:outline-none focus:ring-2 focus:ring-iyaf-blue/30"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 items-center" data-testid="pub-tag-filters">
            <TagIcon size={12} className="text-iyaf-ink/40 mr-1" />
            {publicationTags.map((t) => {
              const active = activeTags.includes(t);
              return (
                <button
                  key={t}
                  onClick={() => toggleTag(t)}
                  data-testid={`pub-tag-${t.toLowerCase().replace(/\s+/g, "-")}`}
                  className="px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all"
                  style={active ? { background: "#3663A8", color: "#fff" } : { background: "white", border: "1px solid rgba(0,0,0,0.08)", color: "rgba(17,24,39,0.65)" }}
                >
                  {t}
                </button>
              );
            })}
            {activeTags.length > 0 && (
              <button onClick={clearTags} data-testid="pub-tag-clear" className="ml-1 inline-flex items-center gap-1 text-[11px] text-iyaf-ink/55 hover:text-iyaf-ink">
                <X size={10} /> Clear
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Result grid */}
      <section className="py-12">
        <div className="container-iyaf">
          <div className="flex items-center justify-between mb-5">
            <p className="text-xs font-mono uppercase tracking-widest text-iyaf-ink/45">
              {list.length} {list.length === 1 ? "publication" : "publications"}
            </p>
          </div>

          {!hasPublished ? (
            <ComingSoonState />
          ) : list.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid md:grid-cols-2 gap-5" data-testid="pub-grid">
              {list.map((p, i) => (
                <PublicationCard key={p.id} pub={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

/* --------------- Publication card --------------- */
function PublicationCard({ pub, index }) {
  const isLandscape = pub.orientation === "landscape";
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.4, delay: index * 0.04 }}
      className="group bg-white rounded-2xl border border-black/5 overflow-hidden hover:shadow-xl hover:shadow-black/5 transition-shadow"
      data-testid={`pub-card-${pub.id}`}
    >
      <div className="flex">
        {/* Cover thumbnail — A4 portrait or landscape */}
        <div className="shrink-0 p-4 bg-iyaf-paper">
          <a
            href={pub.pdf}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`pub-open-${pub.id}`}
            className="block relative shadow-lg shadow-black/15 group-hover:shadow-2xl group-hover:-translate-y-0.5 transition-all"
            style={{
              width: isLandscape ? "160px" : "112px",
              aspectRatio: isLandscape ? "297 / 210" : "1 / 1.414",
            }}
            aria-label={`Open ${pub.title}`}
          >
            {pub.coverImage ? (
              <img src={pub.coverImage} alt={pub.title} className="w-full h-full object-cover rounded-md" />
            ) : (
              <BrandCover pub={pub} />
            )}
            <span className="absolute top-1 right-1 bg-iyaf-ink/85 text-white text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded font-mono">PDF</span>
          </a>
          <div className="mt-2.5 flex items-center justify-center gap-1 text-[9px] font-mono uppercase tracking-widest text-iyaf-ink/45">
            <FileText size={9} />
            <span>{pub.pages}p · A4</span>
          </div>
        </div>

        {/* Details */}
        <div className="flex-1 p-5 flex flex-col min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span
              className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold text-white"
              style={{ background: pub.type === "iyaf" ? "#3663A8" : "#FF9F1C" }}
            >
              {pub.type === "iyaf" ? "IYAF" : "Partner"}
            </span>
            <span className="text-[10px] text-iyaf-ink/55 font-semibold">{pub.source}</span>
            <span className="text-iyaf-ink/20">·</span>
            <span className="text-[10px] font-mono text-iyaf-ink/55">{pub.year}</span>
          </div>

          <h3 className="font-display font-black text-lg leading-tight tracking-tight mb-1.5 text-iyaf-ink line-clamp-2">
            {pub.title}
          </h3>
          <p className="text-[12.5px] text-iyaf-ink/70 leading-snug mb-3 line-clamp-3">{pub.brief}</p>

          <div className="flex flex-wrap gap-1 mb-3">
            {pub.tags.map((t) => (
              <span key={t} className="px-2 py-0.5 rounded-full bg-iyaf-paper border border-black/5 text-[10px] font-semibold text-iyaf-ink/70">
                {t}
              </span>
            ))}
          </div>

          <div className="mt-auto flex items-center gap-2">
            <a
              href={pub.pdf}
              download
              data-testid={`pub-download-${pub.id}`}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-iyaf-ink text-white text-xs font-semibold hover:bg-iyaf-blue transition-colors"
            >
              <Download size={12} /> Download
            </a>
            <a
              href={pub.pdf}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`pub-view-${pub.id}`}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-black/10 text-xs font-semibold text-iyaf-ink/75 hover:text-iyaf-ink hover:bg-iyaf-paper transition-colors"
            >
              View <ExternalLink size={11} />
            </a>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

/* --------------- Brand-styled SVG cover (used when no coverImage provided) --------------- */
function BrandCover({ pub }) {
  const accent = pub.accent || "#3663A8";
  const isLandscape = pub.orientation === "landscape";
  // Simple stylised report cover — branded with arm color + title
  return (
    <div className="absolute inset-0 rounded-md overflow-hidden flex flex-col" style={{ background: "#FDFDFD" }}>
      {/* Top color band */}
      <div className="relative" style={{ height: "32%", background: accent }}>
        <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full bg-white/15" />
        <div className="absolute -left-3 -bottom-3 w-12 h-12 rounded-full bg-white/10" />
        <div className="absolute top-2 left-2 right-2">
          <p className="font-mono text-[6px] uppercase tracking-widest text-white/70">{pub.type === "iyaf" ? "IYAF" : "Partner"} · {pub.year}</p>
        </div>
      </div>
      {/* Title block */}
      <div className="flex-1 p-2 flex flex-col">
        <p className="font-display font-black leading-[1.05] tracking-tight text-iyaf-ink" style={{ fontSize: isLandscape ? "8px" : "9px" }}>
          {pub.title}
        </p>
        <p className="mt-auto font-mono text-[5.5px] uppercase tracking-widest text-iyaf-ink/55">{pub.source}</p>
      </div>
      {/* Brand bar */}
      <div className="px-2 py-1 border-t border-black/5 flex items-center justify-between">
        <IyafWordmark variant="main" size="sm" className="!h-2.5" />
        <span className="font-mono text-[5.5px] text-iyaf-ink/40">A4</span>
      </div>
    </div>
  );
}

/* --------------- Empty / coming soon --------------- */
function ComingSoonState() {
  const { t } = useTranslation();
  return (
    <div className="text-center py-20 bg-white rounded-3xl border border-black/5" data-testid="pub-coming-soon">
      <div className="w-12 h-12 rounded-full bg-iyaf-paper border border-black/5 inline-flex items-center justify-center mb-4">
        <FileText size={20} className="text-iyaf-ink/40" />
      </div>
      <p className="font-display font-black text-xl">{t("publications.emptyTitle")}</p>
      <p className="mt-2 text-sm text-iyaf-ink/60 max-w-md mx-auto">{t("publications.emptyBody")}</p>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="text-center py-20 bg-white rounded-3xl border border-black/5" data-testid="pub-empty">
      <div className="w-12 h-12 rounded-full bg-iyaf-paper border border-black/5 inline-flex items-center justify-center mb-4">
        <FileText size={20} className="text-iyaf-ink/40" />
      </div>
      <p className="font-display font-black text-xl">{t("publications.noMatchTitle", { defaultValue: "No publications match." })}</p>
      <p className="mt-2 text-sm text-iyaf-ink/60">{t("publications.noMatchBody", { defaultValue: "Try removing a tag or clearing the search." })}</p>
    </div>
  );
}
